/**
 * Settings, Your Data: Bring Your Books From QuickBooks (N7 of the #55 plan,
 * §B.11.1.5). An admin chooses the bundle built on their laptop from the
 * QuickBooks Online export (`cli/quickbooks-bundle.ts`), reads the dry run,
 * and presses Import.
 *
 * THE BROWSER KEEPS THE BUNDLE and sends it again with Import, beside the
 * digest the check returned, so what is imported is exactly what was read
 * (round 164's QB-2). The server stores none of it between the two.
 *
 * The dry run shows what would be written, and the opening balance sheet by
 * fund and class beside QuickBooks' own figure, so the operator approves
 * what they have read: that press is the approval CLAUDE.md asks for.
 */

import { useState } from 'react';
import { ApiError, api, type ImportPlan, type ImportResult } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

const CLASS_NAME: Record<ImportPlan['byFund'][number]['netAssetsClass'], string> = {
  without: 'Without Donor Restrictions',
  with: 'With Donor Restrictions',
  retained: 'Retained Earnings',
};

export function QuickBooksImportSection() {
  const [fileName, setFileName] = useState<string | null>(null);
  const [bundle, setBundle] = useState<unknown>(null);
  const [checked, setChecked] = useState<{ digest: string; plan: ImportPlan } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function choose(file: File | undefined) {
    setChecked(null);
    setResult(null);
    setError(null);
    if (file === undefined) return;
    try {
      // The name only once the bundle is read, so "ready to check" is never said of one still reading.
      setBundle(JSON.parse(await file.text()));
      setFileName(file.name);
    } catch {
      setBundle(null);
      setFileName(null);
      setError(`${file.name} is not a bundle: it is not JSON. Build one with the bundle command, then choose it here.`);
    }
  }

  async function check() {
    if (bundle === null) {
      setError('Choose the bundle file first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setChecked(await api.quickbooksCheck(bundle));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not check the bundle.');
    } finally {
      setBusy(false);
    }
  }

  async function runImport() {
    if (checked === null) return;
    if (!checked.plan.ready) {
      setError('The checks refuse this bundle; each reason is listed above. Correct them in QuickBooks or the mapping, build the bundle again, and check it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { result: done } = await api.quickbooksImport(bundle, checked.digest);
      setResult(done);
      setChecked(null);
      setBundle(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not import the bundle.');
    } finally {
      setBusy(false);
    }
  }

  const plan = checked?.plan ?? null;
  const refusals = plan?.findings.filter((finding) => finding.severity === 'refusal') ?? [];
  const notes = plan?.findings.filter((finding) => finding.severity === 'note') ?? [];
  const opened = plan?.byFund.reduce((sum, fund) => sum + fund.netAssetsCents, 0) ?? 0;

  return (
    <section className="settings-section" aria-labelledby="settings-quickbooks">
      <h2 id="settings-quickbooks">Bring Your Books From QuickBooks</h2>
      <p className="muted">
        Opens a new business&rsquo;s books from its QuickBooks Online export, once, before anything is posted.{' '}
        <InfoNote label="How The Import Works">
          Export the reports from QuickBooks, build a bundle from them and the mapping with the bundle command on your own computer,
          then choose it here. Check reads it against this business and writes nothing: every account, program, party, item and
          line it would write, and the opening balance sheet by fund beside QuickBooks&rsquo; figure. Import writes exactly the
          bundle checked, and the opening entry it posts is dated the day the books are brought in as of. History stays in
          QuickBooks. The bundle holds your contacts and any tax id in clear, as the export does; delete it with the export once
          the import has run.
        </InfoNote>
      </p>

      {result !== null && (
        <Toast
          message={`Imported: ${result.lineCount} opening lines, ${result.partiesCreated} parties, ${result.itemsCreated} items, ${result.programsCreated} programs.`}
          variant="ok"
          onDismiss={() => setResult(null)}
        />
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <div className="row">
        <label className="file-pick">
          Bundle{' '}
          <input type="file" accept=".json,application/json" aria-label="QuickBooks bundle" onChange={(event) => void choose(event.target.files?.[0])} />
        </label>
        <button type="button" disabled={busy} onClick={() => void check()}>
          Check The Bundle
        </button>
      </div>
      {fileName !== null && checked === null && result === null && !error && <p className="muted">{fileName} is ready to check.</p>}

      {plan !== null && (
        <div className="import-plan">
          <h3>What Import Would Write, As Of {plan.asOf}</h3>
          {refusals.length > 0 && (
            <div role="alert" className="error">
              <p>The checks refuse this bundle:</p>
              <ul>
                {refusals.map((finding) => (
                  <li key={`${finding.code}-${finding.message}`}>{finding.message}</li>
                ))}
              </ul>
            </div>
          )}
          {notes.map((finding) => (
            <p key={finding.code} className="muted">
              {finding.message}
            </p>
          ))}

          <ul>
            <li>
              {plan.accountsToCreate.length === 0
                ? 'No new accounts.'
                : `New accounts: ${plan.accountsToCreate.map((account) => `${account.code} ${account.name}`).join(', ')}.`}
            </li>
            <li>{plan.programsToCreate.length === 0 ? 'No new programs.' : `New programs: ${plan.programsToCreate.join('; ')}.`}</li>
            <li>
              Parties: {plan.parties.donors + plan.parties.customers} {plan.parties.donors > 0 ? 'donors' : 'customers'}, {plan.parties.vendors} vendors (
              {plan.parties.vendorsFor1099} for Form 1099-NEC){plan.parties.alreadyHere > 0 ? `; ${plan.parties.alreadyHere} already here, kept as they are` : ''}.
            </li>
            <li>Items: {plan.itemsToCreate}.</li>
            {plan.fundsToCreate.length > 0 && <li>New funds: {plan.fundsToCreate.join(', ')}.</li>}
          </ul>

          <h3>Opening Balance Sheet, By Fund</h3>
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th>Net Assets</th>
                  <th className="num">Assets</th>
                  <th className="num">Liabilities</th>
                  <th className="num">Net Assets</th>
                </tr>
              </thead>
              <tbody>
                {plan.byFund.map((fund) => (
                  <tr key={fund.fund ?? 'none'}>
                    <td>{fund.fund ?? 'The Business'}</td>
                    <td>{CLASS_NAME[fund.netAssetsClass]}</td>
                    <td className="num">{formatCents(fund.assetsCents)}</td>
                    <td className="num">{formatCents(fund.liabilitiesCents)}</td>
                    <td className="num">{formatCents(fund.netAssetsCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={opened === plan.foldedFromQuickBooksCents ? 'muted' : 'error'}>
            Net assets opened: {formatCents(opened)}. QuickBooks&rsquo; retained earnings and this year&rsquo;s income:{' '}
            {formatCents(plan.foldedFromQuickBooksCents)}.
          </p>

          <h3>The Opening Entry</h3>
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Fund</th>
                  <th>Item</th>
                  <th className="num">Quantity</th>
                  <th className="num">Debit</th>
                  <th className="num">Credit</th>
                </tr>
              </thead>
              <tbody>
                {plan.lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      {line.accountCode} {line.accountName}
                    </td>
                    <td>{line.fund ?? ''}</td>
                    <td>{line.item ?? ''}</td>
                    <td className="num">{line.quantity ?? ''}</td>
                    <td className="num">{line.debitCents > 0 ? formatCents(line.debitCents) : ''}</td>
                    <td className="num">{line.creditCents > 0 ? formatCents(line.creditCents) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {plan.uncleared.length > 0 && (
            <>
              <h3>Not Yet Cleared At The Bank</h3>
              <p className="muted">
                These are already in the opening bank balance. When the first bank file or feed brings one, dismiss it as already in
                the opening balance rather than recording it again.
              </p>
              <div className="table-scroll">
                <table className="lines">
                  <thead>
                    <tr>
                      <th>Bank Account</th>
                      <th>Date</th>
                      <th>Payee</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.uncleared.map((item, index) => (
                      <tr key={index}>
                        <td>{item.bank}</td>
                        <td>{item.date}</td>
                        <td>{item.payee ?? ''}</td>
                        <td className="num">{formatCents(item.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <button type="button" disabled={busy} onClick={() => void runImport()}>
            Import
          </button>
        </div>
      )}
    </section>
  );
}
